import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useMemorialActive } from "@/components/MourningRibbon";
import { Loader2, Pencil } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { usePageMeta } from "@/lib/seo";
import { UserAvatar } from "@/components/UserBits";
import { timeAgo } from "@/lib/timeAgo";

const Candle = ({ lit, big }) => (
  <span className={`candle ${big ? "candle-big" : ""} ${lit ? "candle-lit" : ""}`} aria-hidden>
    {lit && <span className="candle-flame" />}
    <span className="candle-wick" />
    <span className="candle-body" />
  </span>
);

const EditText = ({ text, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [t, setT] = useState(text);
  const save = async () => {
    try { const { data } = await api.put("/admin/memorial", { text: t }); onSaved(data); setOpen(false); toast.success("Gedenktext gespeichert"); } catch (e) { toast.error(formatErr(e)); }
  };
  if (!open) return <button onClick={() => setOpen(true)} data-testid="memorial-edit" className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-slate-200"><Pencil className="w-3 h-3" /> Text bearbeiten (Admin)</button>;
  return (
    <div className="space-y-2 text-left">
      <textarea value={t} maxLength={600} onChange={(e) => setT(e.target.value)} rows={4} data-testid="memorial-edit-input" className="w-full rounded-2xl bg-white/10 border border-white/20 p-3 text-sm text-white focus:outline-none focus:ring-2 focus:ring-white/30" />
      <div className="flex gap-2 justify-end">
        <button onClick={() => setOpen(false)} className="h-9 px-4 rounded-full text-sm text-slate-300">Abbrechen</button>
        <button onClick={save} disabled={!t.trim()} data-testid="memorial-edit-save" className="h-9 px-4 rounded-full bg-white text-slate-900 text-sm font-bold">Speichern</button>
      </div>
    </div>
  );
};

export default function Memorial() {
  return useMemorialActive() ? <MemorialPage /> : <Navigate to="/feed" replace />;
}

function MemorialPage() {
  usePageMeta({ title: "Gedenken an W. – itzPlay", description: "Zünde eine Kerze für W. an." });
  const { user } = useAuth();
  const [m, setM] = useState(null);
  const [busy, setBusy] = useState(false);
  const [words, setWords] = useState("");
  useEffect(() => { api.get("/memorial").then(({ data }) => setM(data)).catch(() => {}); }, []);
  const light = async () => {
    setBusy(true);
    try { const { data } = await api.post("/memorial/candle", { text: words.trim() || null }); const first = !m.lit; setM(data); setWords(""); toast.success(first ? "Deine Kerze brennt für W. 🕯️" : "Deine Worte sind jetzt bei deiner Kerze 🕯️"); } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  if (!m) return <div className="py-20 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="memorial-page">
      <section className="memorial-hero rounded-[2rem] px-6 py-10 sm:py-14 text-center text-white space-y-5">
        <svg viewBox="0 0 24 32" className="w-8 h-11 mx-auto" aria-hidden>
          <path d="M12 13 C4 4 3 1 7 1 C10 1 12 6 12 13 C12 6 14 1 17 1 C21 1 20 4 12 13 Z" fill="#e5e5e5" />
          <path d="M12 12 L6 31 L9.5 28.5 L11 31.5 L12 14 Z" fill="#bdbdbd" /><path d="M12 12 L18 31 L14.5 28.5 L13 31.5 L12 14 Z" fill="#d4d4d4" />
        </svg>
        <h1 className="text-4xl sm:text-5xl font-display font-extrabold tracking-tight text-slate-100" data-testid="memorial-title">Gedenken an W.</h1>
        <p className="text-slate-300 tracking-[0.3em] text-sm">{m.date}</p>
        <p className="text-slate-200 max-w-xl mx-auto leading-relaxed whitespace-pre-line" data-testid="memorial-text">{m.text}</p>
        {user?.role === "admin" && <EditText text={m.text} onSaved={setM} />}
        <div className="pt-4 flex flex-col items-center gap-4">
          <Candle lit={m.lit} big />
          {m.lit && <p className="text-sm text-amber-200" data-testid="memorial-lit">Deine Kerze brennt 🕯️</p>}
          {m.lit && m.my_text && <p className="text-sm italic text-slate-300 max-w-md" data-testid="memorial-my-text">„{m.my_text}“</p>}
          {!(m.lit && m.my_text) && (
            <div className="w-full max-w-md space-y-3">
              <textarea value={words} maxLength={200} rows={2} onChange={(e) => setWords(e.target.value)} data-testid="memorial-words"
                placeholder={m.lit ? "Möchtest du noch ein paar liebe Worte für W. hinterlassen?" : "Ein paar liebe Worte für W. (freiwillig)"}
                className="w-full rounded-2xl bg-white/10 border border-white/15 px-4 py-3 text-sm text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-200/40 resize-none" />
              <button onClick={light} disabled={busy || (m.lit && !words.trim())} data-testid="memorial-light" className="h-12 px-7 rounded-full bg-amber-200 text-slate-900 font-extrabold shadow-[0_0_30px_rgba(253,230,138,.45)] hover:scale-105 active:scale-95 transition-transform disabled:opacity-50 disabled:hover:scale-100">
                {m.lit ? "Worte hinterlassen" : "Kerze anzünden"}
              </button>
            </div>
          )}
          <p className="text-xs text-slate-400" data-testid="memorial-count">{m.count === 1 ? "1 Kerze brennt" : `${m.count} Kerzen brennen`} für W.</p>
        </div>
      </section>
      {m.candles.length > 0 && (
        <section className="space-y-3">
          <h2 className="text-base md:text-lg font-bold text-slate-700">Sie haben eine Kerze angezündet</h2>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="memorial-candles">
            {m.candles.map((c) => (
              <li key={c.user_id} data-testid={`memorial-candle-${c.user_id}`}>
                <Link to={`/u/${c.user_id}`} className="flex items-start gap-3 p-3 rounded-2xl bg-slate-900 text-white hover:bg-slate-800 transition-colors">
                  <Candle lit />
                  <UserAvatar userId={c.user_id} name={c.name} picture={c.picture} size="w-9 h-9" noStory crown={false} />
                  <span className="min-w-0"><span className="block text-sm font-semibold truncate">{c.name}</span><span className="block text-[11px] text-slate-400">{timeAgo(c.at)}</span>{c.text && <span className="block text-xs italic text-slate-300 mt-1 break-words" data-testid={`memorial-words-${c.user_id}`}>„{c.text}“</span>}</span>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
